import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimTabProps } from '../claim-registration';
import ExcelTable from 'components/ui/excel-table';
import {
  ExcelTableColDef,
  ECellValueType,
  ECellValueDisplayMode,
  EExcelTableColumnType,
  AllRowsData,
  RowData,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { ClaimContractAllocationRow } from './types';
import { TaskIcon } from 'assets/icons/task';
import FormActionButtons from 'components/ui/form-action-buttons';
import { useMutation, useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { binderClaimApi, contractApi } from 'services/apis';
import usePagination from 'hooks/usePagination';
import { message } from 'antd';
import { handleError } from 'utils/form';
import { debounce } from 'lodash';

const ClaimContractAllocation: React.FC<ClaimTabProps> = ({
  claim,
  includeNext,
  navigateNext,
}) => {
  const { t } = useTranslation();

  const { currentPage, pageSize, onPageChange, onShowSizeChange } =
    usePagination(20);

  const {
    data: { items: contractAllocationDataFetched, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading: isLoadingContractAllocation,
  } = useQuery(
    [
      EQueryKeys.CLAIM_CONTRACT_ALLOCATION,
      claim.claimId,
      currentPage,
      pageSize,
    ],
    () => {
      return contractApi.getContractAllocationForClaim({
        insuredId: claim.insuredId,
        lineOfBusiness: claim.lineOfBusiness,
        claimId: claim.claimId,
        isRegistered: false,
        page: currentPage,
        pageSize,
      });
    },
    {
      enabled: !!claim.claimId,
    }
  );

  const [contractAllocationData, setContractAllocationData] = useState<
    ClaimContractAllocationRow[]
  >([]);
  useEffect(() => {
    setContractAllocationData(
      contractAllocationDataFetched.map((item) => ({
        contractId: item.contractId,
        isSelected: false,
        binder: item.binderName,
        broker: item.brokerName,
        reinsured: item.reinsuredName,
        basisOfCover: item.basisOfCover,
        currency: item.currency,
        limit100: Number(item.limit),
        excess100: Number(item.excess),
        shareOfPremiumSigned: Number(item.shareOfPremiumSigned) * 100,
        binderId: item.binderId,
      }))
    );
  }, [contractAllocationDataFetched]);

  const [isAllocationDataValid, setIsAllocationDataValid] = useState(true);

  // Column definitions
  const columnDefs = useMemo(
    (): ExcelTableColDef<ClaimContractAllocationRow>[] => [
      {
        headerName: '',
        headerComponent: () => (
          <div className="flex items-center justify-center w-full h-full">
            <TaskIcon />
          </div>
        ),
        field: 'isSelected',
        width: 100,
        columnType: EExcelTableColumnType.CHECKBOX,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      {
        headerName: t('claim.contractAllocationHeaders.binder'),
        field: 'binder',
        width: 200,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.broker'),
        field: 'broker',
        width: 180,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.reinsured'),
        field: 'reinsured',
        width: 180,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.basisOfCover'),
        field: 'basisOfCover',
        width: 160,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.currency'),
        field: 'currency',
        width: 120,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.limit100'),
        field: 'limit100',
        width: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.excess100'),
        field: 'excess100',
        width: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('claim.contractAllocationHeaders.binderShareSigned'),
        field: 'shareOfPremiumSigned',
        width: 200,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        precision: 4,
        editable: false,
        resizable: true,
      },
      // id
      {
        field: 'contractId',
        hide: true,
      },
      {
        field: 'binderId',
        hide: true,
      },
    ],
    [t]
  );

  const onDataChange = useCallback(
    async (
      _: RowData<ClaimContractAllocationRow>,
      rows: AllRowsData<ClaimContractAllocationRow>
    ) => {
      setContractAllocationData(rows.data);
    },
    []
  );

  const selectedRows = useMemo(() => {
    return contractAllocationData.filter((row) => row.isSelected);
  }, [contractAllocationData]);

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) =>
      setIsAllocationDataValid(!isError.strict),
    []
  );

  const registerClaimContractAllocationMutation = useMutation(
    [EQueryKeys.REGISTER_CLAIM_CONTRACT_ALLOCATION, claim.claimId],
    () => {
      return binderClaimApi.registerBinderClaim({
        createBinderClaimPayloadDto: {
          claimId: claim.claimId,
          claimStatus: claim.status,
          contracts: selectedRows.map((row) => ({
            contractId: row.contractId,
            binderId: row.binderId,
          })),
        },
      });
    },
    {
      onSuccess: () => {
        message.success(t('claim.contractAllocationRegistered'));
        navigateNext();
      },
      onError: (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('claim.contractAllocationRegistrationFailed'),
        });
      },
    }
  );

  const debouncedRegisterClaimContractAllocationMutation = useCallback(
    debounce(() => {
      registerClaimContractAllocationMutation.mutate();
    }, 500),
    [registerClaimContractAllocationMutation]
  );

  return (
    <div className="bg-white p-6 rounded-lg">
      <h2 className="text-xl font-semibold mb-6">Contract Allocation</h2>

      <div className="flex flex-col gap-4">
        <ExcelTable
          onErrorChange={onErrorChange}
          tableContainerClassName={`h-[${
            Math.max(Math.min(contractAllocationData.length, 20), 10) * 3
          }rem]`}
          data={contractAllocationData}
          columnDefs={columnDefs}
          onDataChange={onDataChange}
          rowKeyColName="id"
          getRowClass={(params) => {
            if (params.data && params.data.isSelected) {
              return 'excel-table-row-selected';
            }
            return '';
          }}
          enableErrorBorder
          paginationType={EExcelTablePaginationType.DETAIL}
          onPageChange={onPageChange}
          onPageSizeChange={onShowSizeChange}
          pageSize={pageSize}
          currentPage={currentPage}
          totalRows={total}
          loading={isLoadingContractAllocation}
        />

        <FormActionButtons
          isConfirmDisabled={
            selectedRows.length === 0 || !isAllocationDataValid
          }
          includeCancel={false}
          onConfirm={debouncedRegisterClaimContractAllocationMutation}
          confirmOutlined={false}
          confirmText={t('claim.registerIndividualBinderClaim')}
        />
      </div>
    </div>
  );
};

export default ClaimContractAllocation;
